<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <!-- With the `sidebar-nav` flag on, a signed-in member gets the left
       sidebar instead of the top bar. shell.css holds the frame. -->
  <div
    :class="{
      'gm-shell': isSidebarOn,
      'is-collapsed': isSidebarOn && collapsed,
    }"
  >
    <div
      class="relative flex min-h-screen flex-col"
      :class="{ 'gm-shell-main': isSidebarOn }"
    >
      <div class="flex grow flex-col" :class="{ 'pb-16': !isFooterHidden() }">
        <div id="top-page-content">
          <!-- With the sidebar on, the profile card in the sidebar shows
               these two states, and the page column starts with the page. -->
          <!-- Before the first request (the new mentee experience) the plan
               is the page: no profile bars and no upgrade bar. -->
          <template v-if="areBarsShown && !isBeforeFirstRequest">
            <ProfileIncompleteStatusBar
              v-if="notices.profileIncomplete.value"
            />
            <MentorAutoDeclineStatusBar v-if="notices.profileHidden.value" />
          </template>
          <CurrentUserModeStatusBar v-if="isCurrentUserModeBarShown" />
          <!-- With the sidebar on, a mentor sees the profile that is on as a
               line in its colour: purple for Mentor, ink for Mentee. The
               sidebar card and the phone chip switch between them. -->
          <div
            v-if="isSidebarOn && hasTwoProfiles"
            class="gm-mode-line"
            :class="{ 'is-mentee': isMenteeMode }"
            aria-hidden="true"
          />
          <!-- A new member gets the sidebar from the first visit (Foti,
               27 Oct); the slim top bar stays only without the sidebar. -->
          <SidebarNav v-if="isSidebarOn" />
          <PathTopBar v-else-if="isBeforeFirstRequest" />
          <BaseNavbar v-else />
        </div>

        <NotificationsContainer />

        <!-- The layout paints the canvas: grey, with the pentagon pattern
             at the top, at one height. A page with its own surface, for
             example Matches, names it with `meta.bodyClass`. A layout that
             wraps this one names it with the `canvas` prop. -->
        <main class="gm-app-main flex grow flex-col" :class="canvasClass()">
          <template v-if="!$page.url.startsWith('/thank-you-for-subscribing')">
            <template v-if="isUserLoggedIn">
              <!-- The top-bar layout keeps its bars. With the sidebar on,
                   each of them has a place in the sidebar (useShellNotices),
                   and a phone shows the first one on its Home page only. -->
              <template v-if="areBarsShown">
                <!-- Marketing bars are mutually exclusive: team-invite > upgrade.
                     A mentor applicant in the funnel is not being sold a plan. -->
                <template v-if="isBeforeFirstRequest" />
                <AlertInvitedToTeam v-else-if="notices.teamInvite.value" />
                <AlertUpgradeToPro
                  v-else-if="notices.upgradeKind.value"
                  :kind="notices.upgradeKind.value"
                />
                <!-- Timezone is a functional safety warning (wrong call times); it
                     self-gates on an actual mismatch and must not be hidden behind
                     a marketing bar, or unpaid users never see it. -->
                <AlertTimezone />
              </template>
              <MobileHomeNotice v-else />
            </template>
            <AlertsContainer :is-shown="!isSidebarOn" />
          </template>
          <slot />
        </main>
      </div>
      <BaseFooter
        v-if="!isFooterHidden()"
        class="absolute bottom-0 h-8 w-full"
        :class="{ 'gm-shell-footer': isSidebarOn }"
      />
    </div>

    <FlashToast :shows-all-flashes="isSidebarOn" />
    <UserProfileCompleteModal />
    <UserProfilingWizardModal />
    <FeatureAnnouncementModal />
    <RecordingFeaturePresentationModal />
    <CalendarSyncPromptModal />
    <UnlockFullAccessModal
      v-if="hasRole(ROLE.GUEST) || hasAccess(ACCESS_TYPE.NONE)"
    />

    <!-- Help, bottom left. Signed-in members only: Crisp used to load for
         everyone and identify nobody. With the sidebar on, it opens from there. -->
    <SupportWidget v-if="isUserLoggedIn" :in-sidebar="isSidebarOn" />

    <!-- Instant sessions: the channel, the heartbeat, the mentor's 60 second
         request card and the member's waiting card, on every page. -->
    <InstantSessionsLayer v-if="isUserLoggedIn" />
    <!-- The guide card of the mentor tour (/mentor-tour). -->
    <MentorTourLayer v-if="isMentorTourOn" />
    <!-- The guide card of a feature tour, while one is on. -->
    <FeatureTourHost v-if="isUserLoggedIn" />
  </div>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3'
import BaseNavbar from '@/shared/components/navbar/BaseNavbar.vue'
import SidebarNav from '@/shared/components/sidebar/SidebarNav.vue'
import { useSidebarState } from '@/shared/components/sidebar/useSidebarState'
import { useProfileMode } from '@/shared/components/sidebar/useProfileMode'
import { useFeatureFlags } from '@/shared/composables/useFeatureFlags'
import '@/shared/components/sidebar/shell.css'
import BaseFooter from '@/shared/components/BaseFooter.vue'
import AlertsContainer from '@/shared/components/AlertsContainer.vue'
import FlashToast from '@/shared/components/FlashToast.vue'
import MobileHomeNotice from '@/shared/components/MobileHomeNotice.vue'
import { useShellNotices } from '@/shared/components/sidebar/useShellNotices'
import { useTimezoneSync } from '@/shared/composables/useTimezoneSync'
import NotificationsContainer from '@/shared/components/NotificationsContainer.vue'
import AlertTimezone from '@/shared/components/alerts/AlertTimezone.vue'
import AlertUpgradeToPro from '@/shared/components/alerts/AlertUpgradeToPro.vue'
import CurrentUserModeStatusBar from '@/shared/components/CurrentUserModeStatusBar.vue'
import AlertInvitedToTeam from '@/shared/components/alerts/AlertInvitedToTeam.vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import {
  computed,
  defineAsyncComponent,
  onBeforeMount,
  onMounted,
  useSlots,
  watch,
} from 'vue'
import { useNotificationsStore } from '@/shared/stores/notifications.js'
import { useEnvironmentVariables } from '@/shared/composables/useEnvironmentVariables'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import { Settings } from 'luxon'
import { useDebugger } from '@/shared/composables/useDebugger.js'
import SupportWidget from '@/shared/components/SupportWidget.vue'
import InstantSessionsLayer from '@/modules/instant-sessions/components/InstantSessionsLayer.vue'
import MentorAutoDeclineStatusBar from '@/shared/components/MentorAutoDeclineStatusBar.vue'
import { ACCESS_TYPE, ACTOR_TYPE, ROLE } from '@/shared/data/permissionSystem'
import ProfileIncompleteStatusBar from '@/shared/components/ProfileIncompleteStatusBar.vue'
import UserProfilingWizardModal from '@/shared/components/modals/UserProfilingWizardModal.vue'
import { useModals } from '@/shared/composables/useModals.js'
import {
  CALENDAR_SYNC_PROMPT_MODAL_NAME,
  FEATURE_ANNOUNCEMENT_MODAL_NAME,
  CALENDAR_SYNC_PROMPT_SESSION_KEY,
  RECORDING_FEATURE_PRESENTATION_MODAL_NAME,
  USER_POST_PURCHASE_PROFILING_WIZARD_MODAL_NAME,
  USER_PROFILING_WIZARD_MODAL_NAME,
} from '@/shared/data/globalVariables.js'
import RecordingFeaturePresentationModal from '@/shared/components/modals/RecordingFeaturePresentationModal.vue'
import CalendarSyncPromptModal from '@/shared/components/modals/CalendarSyncPromptModal.vue'
import FeatureAnnouncementModal from '@/shared/components/modals/FeatureAnnouncementModal.vue'
import FeatureTourHost from '@/modules/feature-tour/FeatureTourHost.vue'
import { featureTour } from '@/modules/feature-tour/useFeatureTour'
import UnlockFullAccessModal from '@/shared/components/modals/UnlockFullAccessModal.vue'
import PathTopBar from '@/shared/components/navbar/PathTopBar.vue'
import UserProfileCompleteModal from '@/shared/components/modals/UserProfileCompleteModal.vue'

const { currentUser, timezone } = useCurrentUser()
const { isUserLoggedIn, hasRole, isActorType, hasAccess } =
  usePermissionSystem()
const notificationsStore = useNotificationsStore()
const { SIDEBAR_NAV_ENABLED } = useFeatureFlags()
// The new mentee experience: before the first session request the plan is
// the page (no profile or upgrade bars). The sidebar shows all the same.
const isBeforeFirstRequest = computed(
  () => !!usePage().props.shared?.beforeFirstRequest,
)
const isSidebarOn = computed(
  () => isUserLoggedIn.value && SIDEBAR_NAV_ENABLED.value,
)
const { collapsed } = useSidebarState()
const { hasTwoProfiles, isMenteeMode } = useProfileMode()
const { streamApiKey, streamAppId } = useEnvironmentVariables()
const { $debug } = useDebugger()
const slots = useSlots()

// A layout that wraps this one (MemberOrSiteLayout) sets the canvas for all
// its pages. The page's own meta still wins.
const props = defineProps({
  canvas: { type: String, default: undefined },
})

// A full-bleed page (the roadmap gradient) drops the footer with
// `defineOptions({ meta: { hideFooter: true } })`. Read the flag off the page
// component at render time. A computed caches the first page and goes stale on
// the next Inertia visit, because the slot function is replaced, not reactive.
const isFooterHidden = () =>
  slots.default?.()?.[0]?.type?.meta?.hideFooter === true

// The canvas is grey. A page sets a different one with
// `meta: { bodyClass: 'bg-white' }`, a wrapping layout with the `canvas` prop.
// A function, for the reason above.
const canvasClass = () =>
  slots.default?.()?.[0]?.type?.meta?.bodyClass ?? props.canvas ?? 'gm-canvas'

// The notices that are bars in the top-bar layout and live in the sidebar
// with the sidebar on (useShellNotices).
const notices = useShellNotices()
const timezoneSync = useTimezoneSync()
const areBarsShown = computed(() => !isSidebarOn.value)
// The top bar keeps the "browsing as a Mentee" bar. The sidebar shows the
// profile that is on with its colour instead.
const isCurrentUserModeBarShown = computed(
  () =>
    !isSidebarOn.value &&
    hasRole(ROLE.MENTOR) &&
    isActorType(ACTOR_TYPE.MENTEE),
)
const page = usePage()
// The first visit after a payment (Membership::PaidWelcome) opens nothing by
// itself: the page says the member is in, and that is enough. Read before
// the page clears the prop on mount.
const isPaidWelcomeVisit = !!page.props.paidWelcome
const { openModal } = useModals()

// The mentor tour draws this layout from a recording, for a visitor. The
// demo mode has no notification feed and no time zone sync. Its guide
// card loads only there.
const isMentorTourOn = computed(() => !!page.props.shared.mentorTour)
const MentorTourLayer = defineAsyncComponent(
  () => import('@/modules/mentor-tour/MentorTourLayer.vue'),
)

// The notification feed needs a signed-in member. The Layout persists across
// Inertia visits, so a visitor who signs in without a full page load keeps the
// same Layout instance. The watcher below covers that path; the store makes a
// repeat call a no-op.
const connectNotificationsFeed = () => {
  if (!isUserLoggedIn.value) return
  if (isMentorTourOn.value) return
  if (
    import.meta.env.VITE_DISABLE_NOTIFICATIONS_GETSTREAM_CONNECTION === 'true'
  )
    return

  notificationsStore.initGetStreamClient({
    streamApiKey: streamApiKey.value,
    streamAppId: streamAppId.value,
    userGetStreamToken: currentUser.value.getstreamToken,
    userId: currentUser.value.id,
    subscribeSuccessCallback: () => {
      $debug(
        `[GetStream: ${streamAppId.value}] Notifications channel connected`,
      )
    },
    subscribeErrorCallback: (error) => {
      $debug(
        `[GetStream: ${streamAppId.value}] Error while connecting to notifications channel: ${error}`,
      )
    },
  })
}

watch(isUserLoggedIn, (loggedIn) => {
  if (loggedIn) connectNotificationsFeed()
})

onBeforeMount(() => {
  connectNotificationsFeed()

  // Timezone is always set to local configuration when the Layout component is created,
  // so to synchronise any future changes, like a timezone change in profile settings or timezone synchronisation via an alert,
  // we have to reload the entire page using `window.location.reload()`.
  // You can find all the places where `window.location.reload()` is used in that case
  // by searching for the comment "TIMEZONE SYNCHRONISATION:".
  Settings.defaultZone = timezone.value
})

// With the sidebar on, a member with no availability gets the device's time
// zone at once, and a mentor gets a row in the sidebar. The top-bar layout
// keeps the bar (AlertTimezone). A visitor who signs in keeps this Layout,
// so the watcher covers that path; a repeat call is a no-op.
watch(isSidebarOn, (isOn) => {
  if (isOn && !isMentorTourOn.value) timezoneSync.init()
})

onMounted(() => {
  if (isSidebarOn.value && !isMentorTourOn.value) timezoneSync.init()

  // Disable wheel scroll on input fields (type number)
  document.addEventListener('wheel', function () {
    if (document.activeElement.type === 'number') {
      document.activeElement.blur()
    }
  })

  // One auto-opened modal per page load, first match wins. Two at once share
  // a backdrop and fight over the focus loop. A skipped calendar prompt is
  // not stamped as seen, so its appearance is not spent. No announcement
  // opens over a feature tour: it waits for a later page.
  const autoModal = [
    page.props.shared.profilingWizard &&
      (page.props.shared.profilingWizard.kind === 'post_purchase'
        ? USER_POST_PURCHASE_PROFILING_WIZARD_MODAL_NAME
        : USER_PROFILING_WIZARD_MODAL_NAME),
    page.props.shared.featureAnnouncement &&
      !featureTour.tour &&
      FEATURE_ANNOUNCEMENT_MODAL_NAME,
    page.props.shared.recordableCallsModal &&
      RECORDING_FEATURE_PRESENTATION_MODAL_NAME,
    shouldOpenCalendarSyncPrompt() && CALENDAR_SYNC_PROMPT_MODAL_NAME,
  ].find(Boolean)
  if (autoModal && !isPaidWelcomeVisit) openModal(autoModal)
})

const shouldOpenCalendarSyncPrompt = () => {
  if (!page.props.shared.calendarSyncPrompt) return false
  try {
    return !sessionStorage.getItem(CALENDAR_SYNC_PROMPT_SESSION_KEY)
  } catch {
    return true
  }
}
</script>

<style>
/* The canvas of the member app. The top is white with the pentagon pattern
   and fades into the grey over 275 px. One height on each page. */
.gm-canvas {
  background:
    linear-gradient(to bottom, rgba(241, 244, 249, 0) 0px, #f1f4f9 275px),
    url('@/assets/images/pentagon.svg') top center repeat,
    #fff;
}

/* `container` takes its width from the viewport. With the sidebar on, the
   content area is narrower than the viewport, so the container ran to the
   right edge. Here it has one maximum width and measures the content area.
   A container with its own max-w-* class keeps that width. */
.gm-app-main .container:not([class*='max-w-']) {
  max-width: 75rem;
}
</style>
